// Escompte calculation exactly as EscCalc (~/Developer/EscCalc, src/renderer.js): the reference for the printed
// EscCalc tickets and for „Montant à payer“ after escompte. Every step is rounded like the original
// (round2 with Number.EPSILON); amounts in and out are numbers in euros, as in EscCalc.

export const round2 = (value: number): number => Math.round((value + Number.EPSILON) * 100) / 100;

export interface EscTicket {
  invoiceISO: string; invoiceDate: string;
  net: number; escompte: number; escompteAmount: number; netAfter: number;
  tva: number; tvaAmount: number; total: number;
  delay: number | null; expiry: string;
}

/** DD.MM.YYYY of a YYYY-MM-DD calendar day (no time zone involved). */
export const escDisplayDate = (iso: string): string => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  return m ? `${m[3]}.${m[2]}.${m[1]}` : '—';
};

/** Calendar day + n days, as EscCalc's computeExpiry (local calendar, DD.MM.YYYY). */
export const escExpiry = (iso: string, delay: number): string => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) return '—';
  const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]) + delay));
  return `${String(d.getUTCDate()).padStart(2, '0')}.${String(d.getUTCMonth() + 1).padStart(2, '0')}.${d.getUTCFullYear()}`;
};

/**
 * One EscCalc ticket. `net` = HT before escompte; when only TTC is known, pass `ttc` and the net is derived as in
 * EscCalc (net = round2(ttc / (1 + tva / 100))). `escompte` and `tva` are percentages (2, 17), `delay` calendar days.
 */
export function escTicket(input: { invoiceISO: string; net?: number; ttc?: number; escompte: number; tva: number; delay: number | null }): EscTicket {
  const escompte = round2(input.escompte), tva = round2(input.tva);
  const net = input.net != null ? round2(input.net) : round2((input.ttc ?? 0) / (1 + tva / 100));
  const escompteAmount = round2(net * (escompte / 100));
  const netAfter = round2(net - escompteAmount);
  const tvaAmount = round2(netAfter * (tva / 100));
  const total = round2(netAfter + tvaAmount);
  const delay = input.delay == null ? null : Math.max(0, Math.round(input.delay));
  return {
    invoiceISO: input.invoiceISO, invoiceDate: escDisplayDate(input.invoiceISO),
    net, escompte, escompteAmount, netAfter, tva, tvaAmount, total,
    delay, expiry: delay == null ? '—' : escExpiry(input.invoiceISO, delay),
  };
}

/**
 * Amount after escompte for an invoice with several VAT rates: EscCalc per rate, summed. With one rate this is the
 * ticket's „Total TTC“.
 */
export function escTotal(lines: Array<{ net: number; rate: number }>, escompte: number): number {
  return round2(lines.reduce((sum, l) => sum + escTicket({ invoiceISO: '2000-01-01', net: l.net, escompte, tva: l.rate, delay: 0 }).total, 0));
}

/**
 * „Montant à payer“: what will leave the account for an incoming invoice. `TTC` = the invoice total, `ESCOMPTE` = after
 * escompte (EscCalc), `TVA` = only the VAT (e.g. auto-liquidation, the HT part paid otherwise), `CUSTOM` = typed amount.
 */
export const PAY_MODES = ['TTC', 'ESCOMPTE', 'TVA', 'CUSTOM'] as const;
export type PayMode = typeof PAY_MODES[number];
export const PAY_MODE_LABELS: Record<PayMode, [string, string]> = {
  TTC: ['Rechnungsbetrag (TTC)', 'Montant de la facture (TTC)'],
  ESCOMPTE: ['Nach Escompte', 'Après escompte'],
  TVA: ['Nur TVA', 'TVA seule'],
  CUSTOM: ['Anderer Betrag', 'Autre montant'],
};
